import { DeleteOutlined, EditOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Dropdown, Flex, Image, Input, Modal, Select, Space, Table, Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import new_category_Api from '../../api/new-category';
import projectApi from '../../api/project';
import SH_Toolbar from '../../components/SH_Toolbar';
import TV_new_category_detail from './TV_new_category_detail';

const { Title } = Typography;

const TAG_COLORS = ['magenta', 'red', 'volcano', 'orange', 'gold', 'lime', 'green', 'cyan', 'blue', 'geekblue', 'purple'];

const colorById = (id = '') => {
	let hash = 0;
	for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
	return TAG_COLORS[hash % TAG_COLORS.length];
};

const WrapTag = ({ color, text }) => (
	<Tag
		color={color}
		style={{
			marginInlineEnd: 6,
			maxWidth: 260,
			whiteSpace: 'normal',
			wordBreak: 'break-word',
			lineHeight: '16px',
			paddingTop: 4,
			paddingBottom: 4,
		}}
	>
		{text}
	</Tag>
);

const TV_new_category = () => {
	const [dictionary, setDictionary] = useState({ categories: [], projects: [] });
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState(0);
	const refDetail = useRef();

	const [filter, setFilter] = useState({
		page: 1,
		limit: 10,
		search: '',
		projectIds: [],
	});

	useEffect(() => {
		getProducts(filter);
		getCategory().then((r) => r);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getCategory = async () => {
		setLoading(true);
		try {
			const [categories, projects] = await Promise.all([
				new_category_Api.get({ page: 1, limit: 9999 }),
				projectApi.get({ page: 1, limit: 9999 }),
			]);
			setDictionary({
				categories: categories?.data?.data?.items?.map((x) => ({ ...x, label: x?.names?.vi })) || [],
				projects: projects?.data?.data?.items?.map((x) => ({ ...x, label: x?.names?.vi })) || [],
			});
		} finally {
			setLoading(false);
		}
	};

	const getProducts = (_filter = filter) => {
		setLoading(true);
		new_category_Api
			.get({
				..._filter,
				limit: _filter?.limit || 10,
				page: _filter?.page || 1,
			})
			.then((res) => {
				setProducts(res?.data?.data?.items || []);
				setTotal(res?.data?.data?.pagination?.total || 0);
				setLoading(false);
			});
	};

	const conFirm = (event) => {
		return Modal.confirm({
			title: 'Xác nhận xóa',
			content: 'Bạn có chắc chắn muốn xóa bản ghi này không ? ',
			onOk: () => deleteItem(event),
			okText: 'Xóa',
			cancelText: 'Hủy',
			okButtonProps: { danger: true },
			footer: (_, { OkBtn, CancelBtn }) => (
				<>
					<CancelBtn />
					<OkBtn />
				</>
			),
		});
	};

	const deleteItem = (event) => {
		setLoading(true);
		new_category_Api.delete(event.id).then(() => {
			setLoading(false);
			getProducts(filter);
		});
	};

	const showModal = () => {
		refDetail.current.create();
	};

	const handlePageChange = (page, pageSize) => {
		setFilter((prevState) => ({ ...prevState, page, limit: pageSize }));
		getProducts({ ...filter, page, limit: pageSize });
	};

	const columns = [
		{
			title: 'Ảnh',
			dataIndex: 'image',
			key: 'image',
			width: 120,
			render: (image) =>
				image?.url ? (
					<Image width={90} height={60} style={{ objectFit: 'cover', borderRadius: 10 }} src={image?.url} preview />
				) : (
					<span>-</span>
				),
		},
		{
			title: 'Tên (VI)',
			dataIndex: 'names',
			key: 'names_vi',
			width: 220,
			ellipsis: true,
			render: (names) => <span title={names?.vi}>{names?.vi}</span>,
			sortDirections: ['descend', 'ascend'],
		},
		{
			title: 'Tên (EN)',
			dataIndex: 'names',
			key: 'names_en',
			width: 220,
			ellipsis: true,
			render: (names) => <span title={names?.en}>{names?.en}</span>,
		},

		{
			title: 'Dự án',
			dataIndex: 'projects',
			key: 'projects',
			width: 320,
			render: (_, record) => {
				let list = Array.isArray(record?.projects) ? record.projects : [];

				if (list.length === 0 && Array.isArray(record?.projectIds) && record.projectIds.length > 0) {
					list = record.projectIds
						.map((id) => dictionary?.projects?.find((p) => p?._id === id))
						.filter(Boolean);
				}

				if (!list || list.length === 0) return <span>-</span>;

				return (
					<div style={{ whiteSpace: 'normal' }}>
						<Space size={[6, 6]} wrap>
							{list.map((p) => {
								const nameVi = p?.names?.vi || '';
								return <WrapTag key={p?._id} color={colorById(p?._id)} text={nameVi} />;
							})}
						</Space>
					</div>
				);
			},
		},

		{
			title: 'Ngày tạo',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 190,
			render: (createdAt) => <span>{createdAt ? dayjs(createdAt).format('HH:mm A DD/MM/YYYY') : ''}</span>,
			sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
			sortDirections: ['descend', 'ascend'],
		},
		{
			title: 'Thao tác',
			key: 'action',
			width: 190,
			fixed: 'right',
			render: (_, record) => (
				<Space size={8} wrap>
					<Button size="normal" onClick={() => refDetail.current.update(record)} icon={<EditOutlined />}>
						Sửa
					</Button>
					<Button size="normal" onClick={() => conFirm(record)} type="dashed" danger icon={<DeleteOutlined />}>
						Xoá
					</Button>
				</Space>
			),
		},
	];

	return (
		<>
			<Flex align="center" justify="space-between" wrap="wrap" gap={12} style={{ marginBottom: 12 }}>
				<Title level={2} style={{ margin: 0 }}>
					Loại tin tức
				</Title>
			</Flex>

			<SH_Toolbar
				left={() => (
					<Button icon={<PlusOutlined />} type="primary" onClick={showModal}>
						Thêm loại tin tức
					</Button>
				)}
				right={() => (
					<Flex gap={8} align="center" wrap="wrap">
						<Input.Search
							allowClear
							style={{ width: 240, maxWidth: '100%' }}
							placeholder="Nhập loại tin tức"
							onChange={(e) => {
								const val = e.target.value;
								getProducts({ ...filter, search: val });
								setFilter((prev) => ({ ...prev, search: val }));
							}}
						/>

						<Dropdown
							trigger={['click']}
							placement="bottomRight"
							dropdownRender={() => (
								<div style={{ width: 320, maxWidth: 'calc(100vw - 24px)' }}>
									<div className="bg-white p-4" style={{ borderRadius: 12 }}>
										<div style={{ fontWeight: 600, marginBottom: 8 }}>Dự án</div>
										<Select
											mode="multiple"
											allowClear
											defaultValue={[]}
											style={{ width: '100%' }}
											value={filter.projectIds}
											fieldNames={{ label: 'label', value: '_id' }}
											options={dictionary?.projects}
											onChange={(e) => {
												getProducts({ ...filter, projectIds: e });
												setFilter((prev) => ({ ...prev, projectIds: e }));
											}}
											placeholder="Chọn dự án"
										/>
									</div>
								</div>
							)}
						>
							<Button>
								<Space>
									<FilterOutlined />
									Lọc
								</Space>
							</Button>
						</Dropdown>
					</Flex>
				)}
			/>

			<Table
				rowKey={(record) => record?._id || record?.id}
				tableLayout="fixed"
				pagination={{
					current: filter.page,
					pageSize: filter.limit,
					total: total,
					onChange: handlePageChange,
					showSizeChanger: true,
				}}
				className="mt-2"
				loading={loading}
				columns={columns}
				dataSource={products}
				scroll={{ x: 1300 }}
			/>

			<TV_new_category_detail
				ref={refDetail}
				getProducts={getProducts}
				categories={dictionary?.categories}
				projects={dictionary?.projects}
			/>
		</>
	);
};

export default TV_new_category;
